import * as RadixPopover from '@radix-ui/react-popover'
import classNames from 'classnames'
import { motion } from 'framer-motion'
import { Color, Theme, ZIndex } from '@opallabs/gem'
import { ThemeProvider, useTheme } from '@opallabs/hooks'
import { Button } from '../Button'
import { Icon } from '../Icon'
import { Tooltip, type TooltipProps } from '../Tooltip'
import * as styles from './styles'

// Use this to keep popover content width the same as the anchor
export { radixPopoverAnchorWidthVar } from './styles'

export const Trigger: React.FC<
  Omit<React.ComponentProps<typeof RadixPopover.Trigger>, 'asChild'> & {
    /** @deprecated Backbone implementations only. Use theme provider for React components. */
    legacyTheme?: Theme
    tooltip?: Pick<
      TooltipProps,
      'content' | 'delay' | 'disabled' | 'legacyTheme' | 'sideOffset' | 'zIndex'
    >
    open?: boolean
  }
> = ({ children, tooltip, open, legacyTheme }) => {
  // Radix doesn't trigger opening the popover properly if
  // the trigger is always wrapped in a tooltip
  if (tooltip && !open) {
    return (
      <Tooltip legacyTheme={legacyTheme} {...tooltip}>
        <RadixPopover.Trigger asChild>{children}</RadixPopover.Trigger>
      </Tooltip>
    )
  }
  return <RadixPopover.Trigger asChild>{children}</RadixPopover.Trigger>
}
Trigger.displayName = 'Lapidary: Trigger ← Popover'

const Portal: React.FC<React.ComponentProps<typeof RadixPopover.Portal>> = ({
  children,
  ...props
}) => {
  const theme = useTheme()

  /**
   * RadixPopover.Portal mounts its children outside of the DOM hierarchy, typically
   * directly under the <body> element. Because this will move the components
   * outside of the scope of any CSS variables applied in the existing DOM tree,
   * we need to reprovide Gem's <ThemeProvider> with the current theme to rescope them.
   */
  return (
    <RadixPopover.Portal {...props}>
      <ThemeProvider
        theme={theme}
        // Portalled components should have absolute/fixed position
        // Reset the size properties, so that the portal itself
        // does not affect the container's layout
        css={{ blockSize: 'auto', inlineSize: 'auto' }}
      >
        {children}
      </ThemeProvider>
    </RadixPopover.Portal>
  )
}
Portal.displayName = 'Lapidary: Portal ← Popover'

type ContentProps = React.ComponentProps<typeof RadixPopover.Content> & {
  /**
   * When used within a modal, the focus guard can prevent scrolling via
   * mouse wheels for portaled elements because they are outside the
   * component tree. This allows popovers to skip portalling when needed.
   */
  omitPortal?: boolean
  showArrow?: boolean
  zIndex?: number
}

const ContentWithoutPortal: React.FC<Omit<ContentProps, 'omitPortal'>> = (
  props
) => {
  const theme = useTheme()
  const {
    className,
    showArrow,
    children,
    zIndex = ZIndex.Popover.Body,
    onEscapeKeyDown,
    ...radixProps
  } = props
  return (
    <RadixPopover.Content
      className={classNames('lapidary-popover-content', className)}
      onEscapeKeyDown={(e) => {
        e.stopPropagation()
        onEscapeKeyDown?.(e)
      }}
      style={{ zIndex }}
      {...radixProps}
    >
      <motion.div
        css={styles.baseStyles(theme)}
        animate={{ opacity: 1 }}
        initial={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
      >
        {showArrow && <RadixPopover.Arrow fill={Color[theme].Surface.N000} />}
        {children}
      </motion.div>
    </RadixPopover.Content>
  )
}

export const Content: React.FC<ContentProps> = (props) => {
  const { omitPortal, ...sharedProps } = props

  if (omitPortal) {
    return <ContentWithoutPortal {...sharedProps} />
  }

  return (
    <Portal>
      <ContentWithoutPortal {...sharedProps} />
    </Portal>
  )
}
Content.displayName = 'Lapidary: Content ← Popover'

export const Close: React.FC<
  Omit<React.ComponentProps<typeof RadixPopover.Trigger>, 'asChild'>
> = (props) => (
  <RadixPopover.Close {...props} asChild>
    <Button name="close" icon={<Icon glyph="gem-close" />} variant="ghost" />
  </RadixPopover.Close>
)
Close.displayName = 'Lapidary: Close ← Popover'

export const Popover = {
  Root: RadixPopover.Root,
  Anchor: RadixPopover.Anchor,
  Close,
  Trigger,
  Content,
}
